import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, EmptyState, FieldLabel } from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const FIELDS = [
  { key: "company_name", label: "Company name" },
  { key: "email", label: "Contact email" },
  { key: "footer_tagline", label: "Footer tagline" },
  { key: "phone", label: "Phone (TODO — not publicly shown)" },
  { key: "linkedin_url", label: "LinkedIn URL" },
  { key: "form_recipient_email", label: "Form recipient email" },
  { key: "analytics_provider", label: "Analytics provider (identifier)" },
  { key: "logo_url", label: "Logo URL" },
  { key: "emprenovus_logo_url", label: "Emprenovus logo URL" },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.getSettings().then(setSettings); }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const patch = {};
      FIELDS.forEach((f) => (patch[f.key] = settings[f.key] || null));
      const updated = await adminApi.updateSettings(patch);
      setSettings(updated);
      toast.success("Settings updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <EmptyState title="Loading settings\u2026" />;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="admin-card rounded-sm p-5 md:p-7 space-y-4">
        {FIELDS.map((f) => {
          const isImageField = ["logo_url", "emprenovus_logo_url"].includes(f.key);
          return (
            <div key={f.key} className="block">
              <FieldLabel>{f.label.replace(" URL", "")}</FieldLabel>
              {isImageField ? (
                <ImageUploadField
                  value={settings[f.key] || ""}
                  onChange={(value) => setSettings({ ...settings, [f.key]: value })}
                  category="logo"
                />
              ) : (
                <AdminInput value={settings[f.key] || ""} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })} />
              )}
            </div>
          );
        })}
        <div className="pt-2 flex justify-end">
          <button type="button" onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
            {saving ? "Saving\u2026" : "Save Settings"} <Save className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="admin-card rounded-sm p-5 md:p-7 border border-brand-yellow/20">
        <p className="eyebrow">[ Public Data Rules ]</p>
        <ul className="body-text mt-4 space-y-2 text-sm list-disc pl-5">
          <li>Phone, address and map are hidden publicly until confirmed with the client.</li>
          <li>WhatsApp is intentionally not implemented in this phase.</li>
          <li>LinkedIn will only render in the footer when a valid URL is set here.</li>
          <li>Emprenovus footer branding is required and locked in the design.</li>
        </ul>
      </div>
    </div>
  );
}
